import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { User, Device, Facility, Patient } from '../types';
import {
  getDevices,
  assignDevice,
  uncoupleDevice,
  createDevice,
  getPatients,
  getOuraAuthUrl,
  disconnectOuraDevice,
  syncOuraDevice,
  getAppleHealthApiKey,
  getAppleHealthStatus
} from '../services/api';

interface DevicesProps {
  user: User;
  onLogout: () => void;
  facilities?: Facility[];
  selectedFacility?: string;
  onFacilityChange?: (facilityId: string) => void;
}

const Devices: React.FC<DevicesProps> = ({ user, onLogout, facilities, selectedFacility, onFacilityChange }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [integrationStatuses, setIntegrationStatuses] = useState<Record<string, any>>({});
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [newDeviceType, setNewDeviceType] = useState('');
  const [newDeviceSerialNumber, setNewDeviceSerialNumber] = useState('');

  useEffect(() => {
    loadDevices();
    loadPatients();
  }, [selectedFacility]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const data = await getDevices(selectedFacility);
      setDevices(data);
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const data = await getPatients();
      setPatients(data);
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  };

  const handleAssignClick = (device: Device) => {
    setSelectedDevice(device);
    setSelectedPatientId('');
    setShowAssignModal(true);
  };

  // Filter patients who already have a device of the same type
  const getAvailablePatients = (deviceType: string) => {
    // Get all patient IDs that have a device of this type
    const patientsWithDeviceType = new Set(
      devices
        .filter(d => d.device_type === deviceType && d.patient_id)
        .map(d => d.patient_id)
    );

    // Return patients who don't have this device type
    return patients.filter(p => !patientsWithDeviceType.has(p.id));
  };

  const handleAssignConfirm = async () => {
    if (!selectedDevice || !selectedPatientId) return;

    try {
      setProcessing(true);
      await assignDevice(selectedDevice.id, selectedPatientId);
      await loadDevices();
      setShowAssignModal(false);
      setSelectedDevice(null);
      setSelectedPatientId('');
    } catch (error: any) {
      console.error('Error assigning device:', error);
      alert(error.response?.data?.error || 'Failed to assign device');
    } finally {
      setProcessing(false);
    }
  };

  const handleUncoupleClick = async (device: Device) => {
    if (!window.confirm(`Uncouple ${device.device_type} from ${device.patient_name}?`)) {
      return;
    }

    try {
      setProcessing(true);
      await uncoupleDevice(device.id);
      await loadDevices();
    } catch (error: any) {
      console.error('Error uncoupling device:', error);
      alert(error.response?.data?.error || 'Failed to uncouple device');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddDeviceClick = () => {
    setNewDeviceType('');
    setNewDeviceSerialNumber('');
    setShowAddDeviceModal(true);
  };

  const handleAddDeviceConfirm = async () => {
    if (!newDeviceType || !newDeviceSerialNumber) return;

    try {
      setProcessing(true);
      await createDevice({
        device_type: newDeviceType,
        serial_number: newDeviceSerialNumber,
        facility_id: selectedFacility
      });
      await loadDevices();
      setShowAddDeviceModal(false);
      setNewDeviceType('');
      setNewDeviceSerialNumber('');
      alert('Device added to inventory successfully');
    } catch (error: any) {
      console.error('Error adding device:', error);
      alert(error.response?.data?.error || 'Failed to add device to inventory');
    } finally {
      setProcessing(false);
    }
  };

  // Integration handlers
  const handleOuraConnect = async (device: Device) => {
    try {
      setProcessing(true);
      const { authUrl } = await getOuraAuthUrl(device.id);
      // Open OAuth URL in new window
      window.open(authUrl, 'OuraAuth', 'width=600,height=700');
      // Reload devices after a delay to show updated status
      setTimeout(() => loadDevices(), 3000);
    } catch (error: any) {
      console.error('Error connecting Oura:', error);
      alert(error.response?.data?.error || 'Failed to initiate Oura connection');
    } finally {
      setProcessing(false);
    }
  };

  const handleOuraDisconnect = async (device: Device) => {
    if (!window.confirm('Disconnect this Oura Ring? You will need to reconnect it to sync data.')) {
      return;
    }

    try {
      setProcessing(true);
      await disconnectOuraDevice(device.id);
      await loadDevices();
      alert('Oura Ring disconnected successfully');
    } catch (error: any) {
      console.error('Error disconnecting Oura:', error);
      alert(error.response?.data?.error || 'Failed to disconnect Oura Ring');
    } finally {
      setProcessing(false);
    }
  };

  const handleOuraSync = async (device: Device) => {
    try {
      setProcessing(true);
      await syncOuraDevice(device.id);
      await loadDevices();
      alert('Oura Ring data synced successfully');
    } catch (error: any) {
      console.error('Error syncing Oura:', error);
      alert(error.response?.data?.error || 'Failed to sync Oura data');
    } finally {
      setProcessing(false);
    }
  };

  const handleAppleHealthConnect = async (device: Device) => {
    try {
      setProcessing(true);
      const response = await getAppleHealthApiKey(device.id);
      setApiKey(response.apiKey);
      setSelectedDevice(device);
      setShowApiKeyModal(true);
      await loadDevices();
    } catch (error: any) {
      console.error('Error generating Apple Health API key:', error);
      alert(error.response?.data?.error || 'Failed to generate API key');
    } finally {
      setProcessing(false);
    }
  };

  const isDeviceConnected = (device: Device): boolean => {
    // Check if device has OAuth token (indicates connection)
    return !!(device as any).oauth_access_token;
  };

  // Filter devices based on search and status
  const filteredDevices = devices.filter((device) => {
    const matchesSearch =
      device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.serial_number.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'biostrap':
        return '⌚';
      case 'emfit':
        return '🛏️';
      case 'oura':
        return '💍';
      case 'apple_watch':
        return '⌚';
      case 'withings_bp':
        return '🩺';
      case 'withings_scale':
        return '⚖️';
      default:
        return '📱';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDeviceType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatLastSync = (lastSync: string) => {
    const date = new Date(lastSync);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffMinutes < 1440) {
      return `${Math.floor(diffMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffMinutes / 1440)}d ago`;
    }
  };

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      facilities={facilities}
      selectedFacility={selectedFacility}
      onFacilityChange={onFacilityChange}
    >
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Device Inventory</h1>
            <p className="mt-1 text-sm text-gray-600">
              Monitor and manage all wearable devices
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {filteredDevices.length} Devices
            </span>
            {user?.role === 'admin' && (
              <button
                onClick={handleAddDeviceClick}
                disabled={processing}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Device
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search devices, patients, or serial numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>

        {/* Device List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Loading devices...</div>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">No devices found</div>
            <p className="mt-2 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No devices registered in the system'}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Serial Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Integration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Sync
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDevices.map((device) => (
                  <tr key={device.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{getDeviceTypeIcon(device.device_type)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatDeviceType(device.device_type)}
                          </div>
                          <div className="text-sm text-gray-500">{device.device_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{device.patient_name || <span className="text-gray-400">Unassigned</span>}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 font-mono">{device.serial_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(device.status)}`}>
                        {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(device.device_type === 'oura' || device.device_type === 'apple_watch') ? (
                        <div className="flex items-center space-x-2">
                          {isDeviceConnected(device) ? (
                            <>
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Connected
                              </span>
                              {device.device_type === 'oura' && (
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => handleOuraSync(device)}
                                    disabled={processing}
                                    className="text-blue-600 hover:text-blue-900 disabled:opacity-50 text-xs"
                                    title="Sync now"
                                  >
                                    🔄
                                  </button>
                                  <button
                                    onClick={() => handleOuraDisconnect(device)}
                                    disabled={processing}
                                    className="text-red-600 hover:text-red-900 disabled:opacity-50 text-xs"
                                    title="Disconnect"
                                  >
                                    ❌
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={() => device.device_type === 'oura' ? handleOuraConnect(device) : handleAppleHealthConnect(device)}
                              disabled={processing}
                              className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50"
                            >
                              Connect
                            </button>
                          )}
                        </div>
                      ) : (device.device_type === 'emfit' || device.device_type === 'biostrap') ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Hardware
                        </span>
                      ) : (device.device_type === 'withings_bp' || device.device_type === 'withings_scale') ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          Withings Cloud
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatLastSync(device.last_sync)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {device.patient_name ? (
                        <button
                          onClick={() => handleUncoupleClick(device)}
                          disabled={processing}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          Uncouple
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAssignClick(device)}
                          disabled={processing}
                          className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                        >
                          Assign
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Cards */}
        {!loading && filteredDevices.length > 0 && (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">✅</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Active Devices</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {filteredDevices.filter(d => d.status === 'active').length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">🔧</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Maintenance</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {filteredDevices.filter(d => d.status === 'maintenance').length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">📱</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Device Types</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {new Set(filteredDevices.map(d => d.device_type)).size}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Modal */}
        {showAssignModal && selectedDevice && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Assign Device to Patient
                </h3>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Device: <span className="font-medium">{formatDeviceType(selectedDevice.device_type)}</span>
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Serial: <span className="font-mono font-medium">{selectedDevice.serial_number}</span>
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Patient
                  </label>
                  <select
                    value={selectedPatientId}
                    onChange={(e) => setSelectedPatientId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Select a patient --</option>
                    {getAvailablePatients(selectedDevice.device_type).map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.first_name} {patient.last_name} - Room {patient.room_number}
                      </option>
                    ))}
                  </select>
                  {getAvailablePatients(selectedDevice.device_type).length === 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      All patients already have a {formatDeviceType(selectedDevice.device_type)} device assigned.
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAssignModal(false);
                      setSelectedDevice(null);
                      setSelectedPatientId('');
                    }}
                    disabled={processing}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignConfirm}
                    disabled={processing || !selectedPatientId}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {processing ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Apple Health API Key Modal */}
        {showApiKeyModal && selectedDevice && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Apple Health API Key Generated
                </h3>
                <div className="mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 mb-3">
                      <strong>⌚ Apple Watch Integration Setup</strong>
                    </p>
                    <p className="text-sm text-blue-700 mb-2">
                      Use this API key in your iOS companion app to sync Apple Health data to Edge-on-Age.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key (copy this - it won't be shown again)
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={apiKey}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(apiKey);
                          alert('API key copied to clipboard!');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">iOS App Setup:</p>
                    <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                      <li>Install the Edge-on-Age iOS companion app on your iPhone</li>
                      <li>Enter this API key in the app settings</li>
                      <li>Grant HealthKit permissions when prompted</li>
                      <li>The app will automatically sync your Apple Watch data to Edge-on-Age</li>
                    </ol>
                  </div>

                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">
                      <strong>Important:</strong> Keep this API key secure. Anyone with this key can upload health data for this device.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowApiKeyModal(false);
                      setSelectedDevice(null);
                      setApiKey('');
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Device Modal */}
        {showAddDeviceModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Add Device to Inventory
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Type
                  </label>
                  <select
                    value={newDeviceType}
                    onChange={(e) => setNewDeviceType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Select device type --</option>
                    <option value="apple_watch">⌚ Apple Watch</option>
                    <option value="oura">💍 Oura Ring</option>
                    <option value="emfit">🛏️ Emfit</option>
                    <option value="biostrap">📱 Biostrap</option>
                    <option value="withings_bp">🩺 Withings Blood Pressure</option>
                    <option value="withings_scale">⚖️ Withings Scale</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    value={newDeviceSerialNumber}
                    onChange={(e) => setNewDeviceSerialNumber(e.target.value)}
                    placeholder="Enter device serial number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {newDeviceType && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      {newDeviceType === 'emfit' && (
                        <>
                          <strong>Emfit Device:</strong> Hardware sensor that will be added to the facility inventory. No patient assignment required until installation.
                        </>
                      )}
                      {newDeviceType === 'biostrap' && (
                        <>
                          <strong>Biostrap Device:</strong> Wearable health monitor that can be assigned to patients after enrollment.
                        </>
                      )}
                      {newDeviceType === 'oura' && (
                        <>
                          <strong>Oura Ring:</strong> Sleep and recovery tracker. Patient will need to authorize OAuth access after assignment.
                        </>
                      )}
                      {newDeviceType === 'apple_watch' && (
                        <>
                          <strong>Apple Watch:</strong> Daily activity and vitals tracker. Provides temperature, heart rate, SpO2, and activity data. Patient will need iOS companion app for data sync.
                        </>
                      )}
                      {newDeviceType === 'withings_bp' && (
                        <>
                          <strong>Withings Blood Pressure Monitor:</strong> Connected BP cuff for tracking blood pressure and heart rate. Required for blood pressure trend monitoring.
                        </>
                      )}
                      {newDeviceType === 'withings_scale' && (
                        <>
                          <strong>Withings Smart Scale:</strong> Connected scale for tracking weight and body composition metrics.
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAddDeviceModal(false);
                      setNewDeviceType('');
                      setNewDeviceSerialNumber('');
                    }}
                    disabled={processing}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddDeviceConfirm}
                    disabled={processing || !newDeviceType || !newDeviceSerialNumber}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {processing ? 'Adding...' : 'Add Device'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Devices;
